import React, { useEffect, useMemo, useState } from 'react';
import { Grid, InputAdornment, MenuItem, TextField } from '@material-ui/core';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { PrimaryButton } from '@/styles/blocks';
import { ASSIGN_ALL_ORDER_TYPES, ASSIGN_ZIP_TYPES, ASSIGNABLE_ORDER_TYPES } from './constants';

const ORDER_TYPE_OPTIONS = [{ value: ASSIGN_ALL_ORDER_TYPES, label: 'All order types' }, ...ASSIGNABLE_ORDER_TYPES];
const ORDER_TYPE_LABELS = Object.fromEntries(ORDER_TYPE_OPTIONS.map((o) => [o.value, o.label]));

const ApplyToShipperBar = ({
    shippers,
    deliveryProfileId,
    deliveryProfile,
    assignmentsByShipper,
    applyToShipper,
    onSuccess,
    disabled,
}) => {
    const [selectedShipperId, setSelectedShipperId] = useState('');
    const [orderTypes, setOrderTypes] = useState([ASSIGN_ALL_ORDER_TYPES]);
    const [zipType, setZipType] = useState('DROPOFF');

    // Reset the selection to defaults whenever the active profile changes.
    useEffect(() => {
        setOrderTypes([ASSIGN_ALL_ORDER_TYPES]);
        setZipType('DROPOFF');
    }, [deliveryProfileId]);

    const canApply = !!selectedShipperId && !!deliveryProfileId && orderTypes.length > 0 && !disabled;

    // A profile is "applied" to a shipper if any assignment row links them —
    // regardless of which order_type(s) it covers.
    const isApplied = (s) =>
        !!s && (assignmentsByShipper?.[s.client_id] || []).some((a) => a.delivery_profile_id === deliveryProfileId);

    const selectedShipperApplied = useMemo(
        () => isApplied(shippers.find((sh) => sh.client_id === selectedShipperId)),
        [shippers, selectedShipperId, deliveryProfileId, assignmentsByShipper]
    );

    // "All order types" is mutually exclusive with the specific values.
    const onOrderTypesChange = (e) => {
        const next = e.target.value;
        const hadAll = orderTypes.includes(ASSIGN_ALL_ORDER_TYPES);
        const hasAll = next.includes(ASSIGN_ALL_ORDER_TYPES);
        if (hasAll && !hadAll) setOrderTypes([ASSIGN_ALL_ORDER_TYPES]);
        else if (hasAll && next.length > 1) setOrderTypes(next.filter((v) => v !== ASSIGN_ALL_ORDER_TYPES));
        else setOrderTypes(next);
    };

    const onApply = async () => {
        await applyToShipper(selectedShipperId, deliveryProfile, orderTypes, zipType);
        onSuccess?.();
    };

    return (
        <Grid
            container
            css={css`
                /* 12px top + HeaderBar's 0 bottom = 12px gap above us.
                   0 bottom + SubregionList search's 12px top = 12px gap below us.
                   Matches the 12px spacing used between rows in the zips column. */
                padding: 12px 20px 0px;
                /* Wraps to two rows in the 400px column — the selects hit their
                   min-widths before they can collapse under their own labels. */
                column-gap: 16px;
                row-gap: 12px;
                flex-wrap: wrap;
                align-items: center;
                background: ${colors.white.primary};
            `}
        >
            <Grid
                item
                css={css`
                    flex: 1 1 160px;
                    min-width: 160px;
                `}
            >
                <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Shipper"
                    id="apply-to-shipper-select"
                    InputLabelProps={{ htmlFor: 'apply-to-shipper-select' }}
                    value={selectedShipperId}
                    onChange={(e) => setSelectedShipperId(e.target.value)}
                    SelectProps={{
                        // Without this, MUI renders the selected MenuItem's full children
                        // (business_name + the dropdown's CheckCircle) in the trigger, which
                        // collides with the startAdornment we add for the same purpose.
                        renderValue: (id) => shippers.find((s) => s.client_id === id)?.business_name || '',
                    }}
                    InputProps={{
                        // Adornment slot is sized by MUI to fit the input height — the icon
                        // sits next to the value without participating in the value's line-box,
                        // so the input height stays locked regardless of icon presence.
                        startAdornment: selectedShipperApplied ? (
                            <InputAdornment position="start">
                                <CheckCircle
                                    css={css`
                                        color: ${colors.greens.primary};
                                        font-size: 16px;
                                    `}
                                />
                            </InputAdornment>
                        ) : null,
                    }}
                >
                    {shippers.map((s) => {
                        const applied = isApplied(s);
                        const Icon = applied ? CheckCircle : RadioButtonUnchecked;
                        return (
                            <MenuItem key={s.client_id} value={s.client_id}>
                                <Icon
                                    css={css`
                                        color: ${applied ? colors.greens.primary : colors.greys[2]};
                                        font-size: 16px;
                                        margin-right: 8px;
                                    `}
                                />
                                {s.business_name}
                            </MenuItem>
                        );
                    })}
                </TextField>
            </Grid>
            <Grid
                item
                css={css`
                    flex: 1 1 160px;
                    min-width: 160px;
                `}
            >
                <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Order Types"
                    id="apply-order-types-select"
                    InputLabelProps={{ htmlFor: 'apply-order-types-select' }}
                    value={orderTypes}
                    onChange={onOrderTypesChange}
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => selected.map((v) => ORDER_TYPE_LABELS[v]).join(', '),
                        MenuProps: {
                            // MUI v4 Menu defaults to variant="selectedMenu", which anchors the
                            // popover on the last selected item. The menu stays open across
                            // selections here, so it would creep upward on every click.
                            variant: 'menu',
                            getContentAnchorEl: null,
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                        },
                    }}
                >
                    {ORDER_TYPE_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                            {o.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid
                item
                css={css`
                    flex: 1 1 140px;
                    min-width: 140px;
                `}
            >
                <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Assign Zip By"
                    id="apply-zip-type-select"
                    InputLabelProps={{ htmlFor: 'apply-zip-type-select' }}
                    value={zipType}
                    onChange={(e) => setZipType(e.target.value)}
                >
                    {ASSIGN_ZIP_TYPES.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                            {o.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid
                item
                css={css`
                    flex-shrink: 0;
                `}
            >
                <PrimaryButton
                    onClick={onApply}
                    disabled={!canApply}
                    css={css`
                        height: 40px;
                        width: 96px;
                        min-width: 0;
                        padding: 6px 16px;
                    `}
                >
                    Apply
                </PrimaryButton>
            </Grid>
        </Grid>
    );
};

export default ApplyToShipperBar;
